import { useState, useEffect, useRef } from "react";
import { useParams, useRouter, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCreateWordsBulk } from "@/lib/api/vocabulary";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import {
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Eraser,
  Trash2,
} from "lucide-react";
import type { Category } from "@/lib/api/vocabulary/types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableRow } from "./_components/-sortable-row";
import { UploadResultDialog } from "./_components/-upload-result-dialog";
import { PageValidationAlert } from "./_components/-page-validation-alert";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

interface ParsedWord {
  id: number;
  word: string;
  meaning: string;
  page: number;
  number: number;
}

function CSVUploadPage() {
  const params = useParams({ strict: false });
  const router = useRouter();
  const categoryId = params.id as string;

  const [parsedWords, setParsedWords] = useState<ParsedWord[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: number;
    failed: number;
    errors: Array<{ index: number; error: string }>;
  } | null>(null);

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const prevPageRef = useRef(currentPage);

  const { mutateAsync: createWordsBulk, isPending: isUploading } =
    useCreateWordsBulk();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  // 카테고리 정보 가져오기
  const { data: category } = useQuery({
    queryKey: ["category", categoryId],
    queryFn: async () => {
      const response = await apiClient.get<Category>(
        `/categories/${categoryId}`
      );
      return response.data;
    },
  });

  const wordsPerPage = category?.words_per_page || 25;

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 페이지가 변경되면 첫 input에 포커스
  useEffect(() => {
    if (currentPage !== prevPageRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
    prevPageRef.current = currentPage;
  }, [currentPage]);

  // CSV 템플릿 다운로드
  const handleDownloadTemplate = () => {
    const csvContent = "word,meaning\napple,사과\nbook,책\ncat,고양이";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "vocabulary_template.csv";
    link.click();
  };

  // CSV 파일 파싱 (UTF-8, EUC-KR 자동 감지)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const buffer = event.target?.result as ArrayBuffer;

      // UTF-8로 먼저 디코딩 시도
      const utf8Decoder = new TextDecoder("utf-8");
      const utf8Text = utf8Decoder.decode(buffer);

      // UTF-8 디코딩 결과에 replacement character(�)가 있으면 EUC-KR로 재시도
      if (utf8Text.includes("\uFFFD")) {
        const eucKrDecoder = new TextDecoder("euc-kr");
        const eucKrText = eucKrDecoder.decode(buffer);
        parseCSV(eucKrText);
      } else {
        parseCSV(utf8Text);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // CSV 라인을 파싱하여 필드 배열로 반환 (큰따옴표 내 쉼표 처리)
  const parseCSVLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // 큰따옴표 이스케이프 ("") 처리
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        fields.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    fields.push(current.trim());
    return fields;
  };

  const parseCSV = (text: string) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      toast.error("CSV 파일이 비어있습니다.");
      return;
    }

    const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase());
    if (!headers.includes("word") || !headers.includes("meaning")) {
      toast.error('CSV 파일에 "word", "meaning" 열이 필요합니다.');
      return;
    }

    const wordIndex = headers.indexOf("word");
    const meaningIndex = headers.indexOf("meaning");

    const words: ParsedWord[] = [];
    let idCounter = 1;

    // 기존 카테고리의 마지막 페이지를 기준으로 시작 페이지 계산
    // last_page가 0이면 빈 카테고리이므로 1부터 시작, 아니면 last_page + 1부터 시작
    const startPage =
      category && category.last_page > 0 ? category.last_page + 1 : 1;

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length < 2) continue;

      const wordNumber = i;
      const page = startPage + Math.floor((wordNumber - 1) / wordsPerPage);
      const number = ((wordNumber - 1) % wordsPerPage) + 1;

      words.push({
        id: idCounter++,
        word: values[wordIndex],
        meaning: values[meaningIndex],
        page,
        number,
      });
    }

    setParsedWords(words);
    setCurrentPage(startPage);
  };

  // 페이지별 단어 개수 초과 체크
  const getPageOverflowErrors = (): string[] => {
    const errors: string[] = [];
    const pageGroups: Record<number, number> = {};

    parsedWords.forEach((word) => {
      pageGroups[word.page] = (pageGroups[word.page] || 0) + 1;
    });

    Object.entries(pageGroups).forEach(([page, count]) => {
      if (count !== wordsPerPage) {
        errors.push(
          `Page ${page}: ${count}개 단어 (페이지별 단어 개수: ${wordsPerPage}개)`
        );
      }
    });

    return errors;
  };

  // 빈 단어 체크
  const hasEmptyWords = parsedWords.some(
    (word) => !word.word.trim() || !word.meaning.trim()
  );

  // 페이지 개수 초과 에러
  const pageOverflowErrors = getPageOverflowErrors();
  const hasPageOverflow = pageOverflowErrors.length > 0;

  // 업로드 가능 여부
  const canUpload = !hasEmptyWords && !hasPageOverflow;

  // 단어 변경
  const handleWordChange = (
    id: number,
    field: "word" | "meaning",
    value: string
  ) => {
    setParsedWords((prev) =>
      prev.map((w) => (w.id === id ? { ...w, [field]: value } : w))
    );
  };

  // 단어 비우기
  const handleClearWord = (id: number) => {
    setParsedWords((prev) =>
      prev.map((w) => (w.id === id ? { ...w, word: "", meaning: "" } : w))
    );
  };

  // 단어 위로 이동
  const handleMoveWordUp = (id: number) => {
    const currentWordList = parsedWords.filter((w) => w.page === currentPage);
    const index = currentWordList.findIndex((w) => w.id === id);
    if (index <= 0) return;

    const newWords = [...parsedWords];
    const globalIndex = newWords.findIndex((w) => w.id === id);
    const prevGlobalIndex = newWords.findIndex(
      (w) => w.id === currentWordList[index - 1].id
    );

    [newWords[globalIndex], newWords[prevGlobalIndex]] = [
      newWords[prevGlobalIndex],
      newWords[globalIndex],
    ];

    // number 재할당
    const temp = newWords[globalIndex].number;
    newWords[globalIndex].number = newWords[prevGlobalIndex].number;
    newWords[prevGlobalIndex].number = temp;

    setParsedWords(newWords);
  };

  // 단어 아래로 이동
  const handleMoveWordDown = (id: number) => {
    const currentWordList = parsedWords.filter((w) => w.page === currentPage);
    const index = currentWordList.findIndex((w) => w.id === id);
    if (index >= currentWordList.length - 1) return;

    const newWords = [...parsedWords];
    const globalIndex = newWords.findIndex((w) => w.id === id);
    const nextGlobalIndex = newWords.findIndex(
      (w) => w.id === currentWordList[index + 1].id
    );

    [newWords[globalIndex], newWords[nextGlobalIndex]] = [
      newWords[nextGlobalIndex],
      newWords[globalIndex],
    ];

    // number 재할당
    const temp = newWords[globalIndex].number;
    newWords[globalIndex].number = newWords[nextGlobalIndex].number;
    newWords[nextGlobalIndex].number = temp;

    setParsedWords(newWords);
  };

  // 드래그 앤 드롭으로 재정렬
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentWordList = parsedWords.filter((w) => w.page === currentPage);
    const oldIndex = currentWordList.findIndex((w) => w.id === active.id);
    const newIndex = currentWordList.findIndex((w) => w.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newWords = [...parsedWords];
    const oldGlobalIndex = newWords.findIndex((w) => w.id === active.id);
    const newGlobalIndex = newWords.findIndex((w) => w.id === over.id);

    // 배열에서 요소 제거 및 삽입
    const [removed] = newWords.splice(oldGlobalIndex, 1);
    newWords.splice(newGlobalIndex, 0, removed);

    // number 재할당 (현재 페이지만)
    const updatedPageWords = newWords.filter((w) => w.page === currentPage);
    updatedPageWords.forEach((word, idx) => {
      word.number = idx + 1;
    });

    setParsedWords(newWords);
  };

  // 현재 페이지 비우기
  const handleClearCurrentPage = () => {
    setAlertDialogState({
      title: "페이지 비우기",
      description: "현재 페이지의 모든 단어를 비우시겠습니까?",
      confirmText: "비우기",
      onConfirm: () => {
        resetAlertDialogState();
        setParsedWords((prev) =>
          prev.map((w) =>
            w.page === currentPage ? { ...w, word: "", meaning: "" } : w
          )
        );
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  // 페이지 삭제
  const handleDeletePage = (page: number) => {
    setAlertDialogState({
      title: "페이지 삭제",
      description: `Page ${page}를 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: () => {
        resetAlertDialogState();

        const newWords = parsedWords.filter((w) => w.page !== page);

        // 기존 카테고리의 마지막 페이지를 기준으로 시작 페이지 계산
        const startPage =
          category && category.last_page > 0 ? category.last_page + 1 : 1;

        // 페이지 번호 재할당
        const reindexedWords = newWords.map((word, index) => {
          const newPage = startPage + Math.floor(index / wordsPerPage);
          const newNumber = (index % wordsPerPage) + 1;
          return { ...word, page: newPage, number: newNumber };
        });

        setParsedWords(reindexedWords);

        // 현재 페이지 조정
        const minPage =
          reindexedWords.length > 0
            ? Math.min(...reindexedWords.map((w) => w.page))
            : startPage;
        if (
          currentPage > minPage &&
          reindexedWords.every((w) => w.page < currentPage)
        ) {
          setCurrentPage(Math.max(minPage, currentPage - 1));
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  // 서버로 업로드
  const handleUpload = async () => {
    if (parsedWords.length === 0) return;

    try {
      const result = await createWordsBulk({
        categoryId,
        words: parsedWords.map((word) => ({
          category: categoryId,
          word: word.word,
          meaning: word.meaning,
          page: word.page,
          number: word.number,
        })),
      });

      setUploadResult(result);
      setIsResultDialogOpen(true);
    } catch {
      toast.error("업로드 중 오류가 발생했습니다.");
    }
  };

  // 결과 Dialog 닫기 및 초기화
  const handleCloseResultDialog = () => {
    setIsResultDialogOpen(false);
    if (uploadResult && uploadResult.failed === 0) {
      // 성공한 경우 페이지 이동
      router.history.back();
    } else {
      // 실패가 있는 경우 처음부터 다시 시작
      setParsedWords([]);
      setCurrentPage(1);
      setUploadResult(null);
    }
  };

  // 현재 페이지의 단어 목록
  const currentWordList = parsedWords.filter((w) => w.page === currentPage);
  const minPage =
    parsedWords.length > 0 ? Math.min(...parsedWords.map((w) => w.page)) : 1;
  const maxPage =
    parsedWords.length > 0 ? Math.max(...parsedWords.map((w) => w.page)) : 1;

  // 페이지 번호 리스트 생성 (최대 3개)
  const getPageNumberList = () => {
    const totalPages = maxPage - minPage + 1;

    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => minPage + index);
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start < minPage) {
      start = minPage;
      end = Math.min(minPage + 2, maxPage);
    }

    if (end > maxPage) {
      end = maxPage;
      start = Math.max(maxPage - 2, minPage);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const pageNumberList = getPageNumberList();

  // 키보드 단축키 핸들러
  const handleKeyDown = (
    e: React.KeyboardEvent,
    field: "word" | "meaning",
    wordId: number
  ) => {
    const currentWordList = parsedWords.filter((w) => w.page === currentPage);
    const index = currentWordList.findIndex((w) => w.id === wordId);
    const isFirstInPage = index === 0;
    const isLastInPage = index === currentWordList.length - 1;

    // Alt + ArrowUp: 데이터를 위로 이동하고 포커스도 따라감
    if (e.altKey && e.key === "ArrowUp") {
      e.preventDefault();
      if (!isFirstInPage) {
        handleMoveWordUp(wordId);
        // 데이터가 이동한 후 현재 wordId는 index-1 위치로 이동
        setTimeout(() => {
          const input = document.querySelector(
            `input[data-word-id="${wordId}"][data-field="${field}"]`
          ) as HTMLInputElement;
          input?.focus();
        }, 50);
      }
    }
    // Alt + ArrowDown: 데이터를 아래로 이동하고 포커스도 따라감
    else if (e.altKey && e.key === "ArrowDown") {
      e.preventDefault();
      if (!isLastInPage) {
        handleMoveWordDown(wordId);
        // 데이터가 이동한 후 현재 wordId는 index+1 위치로 이동
        setTimeout(() => {
          const input = document.querySelector(
            `input[data-word-id="${wordId}"][data-field="${field}"]`
          ) as HTMLInputElement;
          input?.focus();
        }, 50);
      }
    }
    // Alt + Delete: 현재 행 비우기
    else if (e.altKey && e.key === "Delete") {
      e.preventDefault();
      handleClearWord(wordId);
    }
    // ArrowUp: 위 행으로 포커스 이동
    else if (e.key === "ArrowUp" && !e.altKey) {
      e.preventDefault();
      if (index > 0) {
        const prevWord = currentWordList[index - 1];
        const prevInput = document.querySelector(
          `input[data-word-id="${prevWord.id}"][data-field="${field}"]`
        ) as HTMLInputElement;
        prevInput?.focus();
      }
    }
    // ArrowDown: 아래 행으로 포커스 이동
    else if (e.key === "ArrowDown" && !e.altKey) {
      e.preventDefault();
      if (index < currentWordList.length - 1) {
        const nextWord = currentWordList[index + 1];
        const nextInput = document.querySelector(
          `input[data-word-id="${nextWord.id}"][data-field="${field}"]`
        ) as HTMLInputElement;
        nextInput?.focus();
      }
    }
  };

  return (
    <PageContainer>
      <Header
        title="단어 업로드"
        description="CSV 파일로 단어를 업로드합니다."
      />

      <div className="space-y-6">
        {/* 템플릿 다운로드 & 파일 업로드 */}
        <div className="flex gap-4 justify-end sm:justify-start">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            템플릿 다운로드
          </Button>
          <div>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button
                variant="default"
                className="flex items-center gap-2"
                onClick={() => document.getElementById("csv-upload")?.click()}
                type="button"
              >
                <Upload className="w-4 h-4" />
                파일 업로드
              </Button>
            </label>
          </div>
        </div>

        {/* 단어 테이블 */}
        {parsedWords.length > 0 && (
          <>
            {/* 페이지 개수 초과 경고 */}
            <PageValidationAlert errors={pageOverflowErrors} />

            <div className="flex flex-col gap-2 border border-input rounded-md">
              {/* 페이지네이션 & 도움말 */}
              <div className="flex justify-between items-center pt-4 px-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage === minPage}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumberList.map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      variant={
                        pageNumber === currentPage ? "default" : "outline"
                      }
                      size="icon"
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage === maxPage}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-semibold">키보드 단축키</h4>
                      <ul className="text-sm space-y-1">
                        <li>
                          •{" "}
                          <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                            ↑/↓
                          </kbd>{" "}
                          위/아래 행으로 이동
                        </li>
                        <li>
                          •{" "}
                          <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                            Alt
                          </kbd>{" "}
                          +{" "}
                          <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                            ↑/↓
                          </kbd>{" "}
                          데이터를 위/아래로 이동
                        </li>
                        <li>
                          •{" "}
                          <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                            Alt
                          </kbd>{" "}
                          +{" "}
                          <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">
                            Del
                          </kbd>{" "}
                          현재 행 비우기
                        </li>
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* 드래그 가능한 테이블 */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table className="min-w-xl">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center w-24">No.</TableHead>
                      <TableHead className="text-center">Word</TableHead>
                      <TableHead className="text-center">Meaning</TableHead>
                      <TableHead className="text-center w-40">동작</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext
                      items={currentWordList.map((w) => w.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {currentWordList.map((word, index) => {
                        const isFirstInPage = index === 0;
                        const isLastInPage =
                          index === currentWordList.length - 1;

                        return (
                          <SortableRow
                            key={word.id}
                            word={word}
                            isFirstInPage={isFirstInPage}
                            isLastInPage={isLastInPage}
                            firstInputRef={
                              isFirstInPage ? firstInputRef : undefined
                            }
                            onWordChange={handleWordChange}
                            onClearWord={handleClearWord}
                            onMoveWordUp={handleMoveWordUp}
                            onMoveWordDown={handleMoveWordDown}
                            handleKeyDown={handleKeyDown}
                          />
                        );
                      })}
                    </SortableContext>
                  </TableBody>
                </Table>
              </DndContext>

              {/* 페이지 관리 버튼 */}
              <div className="flex justify-center gap-2 px-4 pb-4 flex-wrap">
                <Button
                  onClick={handleClearCurrentPage}
                  size="sm"
                  variant="outline"
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  현재 페이지 비우기
                </Button>
                <Button
                  onClick={() => handleDeletePage(currentPage)}
                  size="sm"
                  variant="outline"
                  disabled={minPage === maxPage}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  현재 페이지 삭제
                </Button>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              총 {parsedWords.length}개 단어 · {maxPage}페이지
            </div>

            <Button
              onClick={handleUpload}
              disabled={isUploading || !canUpload}
              className="w-full"
              size="lg"
            >
              {isUploading ? "업로드 중..." : "단어 업로드"}
            </Button>

            {!canUpload && !isUploading && (
              <div className="text-sm text-gray-600 text-center">
                {hasEmptyWords && "빈 단어나 뜻이 있습니다. "}
                {hasPageOverflow && "페이지별 단어 개수를 확인해주세요."}
              </div>
            )}
          </>
        )}

        {/* 업로드 결과 Dialog */}
        <UploadResultDialog
          open={isResultDialogOpen}
          onOpenChange={setIsResultDialogOpen}
          result={uploadResult}
          parsedWords={parsedWords}
          onClose={handleCloseResultDialog}
        />
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/vocabulary/categories/$id/words/upload")({
  component: CSVUploadPage,
});
